class CascadingGrid extends HTMLElement {
  constructor() {
    super();
    
    this.observer = null;
    this.masonryInstance = null;
  }

  connectedCallback() {
    requestAnimationFrame(() => this.initGrid());

    document.addEventListener('shopify:section:load', () => {
      setTimeout(() => this.initGrid(), 100);
    });
  }

  initGrid() {
    const grid = this.querySelector('.grid');
    if (!grid) return;

    if (this.masonryInstance) {
      this.masonryInstance.destroy();
    }

    this.masonryInstance = new Masonry(grid, {
      itemSelector: '.grid-item'
    });

    grid.masonryInstance = this.masonryInstance;

    this.observeLazyContent(grid);
  }

  observeLazyContent(target) {
    if (this.observer) {
      this.observer.disconnect();
    }

    this.observer = new MutationObserver(mutations => {
      let shouldRelayout = false;

      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          shouldRelayout = true;
          break;
        }
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          if (mutation.target.classList.contains('lazyloaded')) {
            shouldRelayout = true;
            break;
          }
        }
      }

      if (shouldRelayout && this.masonryInstance) {
        this.masonryInstance.layout();
      }
    });

    this.observer.observe(target, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }
}

customElements.define('cascading-grid', CascadingGrid);

class MultiSwiper {
  constructor(selector, options) {
    this.swipers = []
    this.selector = selector
    this.options = options
    document.addEventListener('swiper:update', () => {this.initSwipers()})
    this.initSwipers()
    document.addEventListener('shopify:section:load', () => {this.initSwipers()})
  }

  initSwipers() {
    this.destroyAllSwipers();
    document.querySelectorAll(this.selector).forEach(container => {
        const swiper = new Swiper(container, this.options)
        this.swipers.push(swiper)
        this.setupAutoplay(container, swiper)
        this.setupBullets(container, swiper)
        window.addEventListener('resize', () => {
          this.setupAutoplay(container, swiper)
          this.setupBullets(container, swiper)
        })
    })
  }

  removeSlide(index, swiperIndex = 0) {
    const swiper = this.swipers[swiperIndex];

    if (swiper) {
      swiper.removeSlide(index);
      swiper.update();
      swiper.slideTo(0, 0, false); 
    }
  }

  destroyAllSwipers() {
    this.swipers.forEach(swiper => swiper.destroy(true, true));
    this.swipers = [];
  }

  setupAutoplay(container, swiper) {
    if (container.dataset.autoplay === 'true' && container.dataset.hoverAutoplay === 'true') {
          const autoplaySpeed = parseInt(container.dataset.autoplaySpeed, 10) || 5000
          container.addEventListener('mouseenter', () => {
              if (window.innerWidth > 768) {
                  swiper.params.autoplay = {
                      delay: autoplaySpeed,
                      disableOnInteraction: false
                  };
                  swiper.autoplay.start()
              }
          });
          container.addEventListener('mouseleave', () => {
              if (swiper.autoplay.running) {
                  swiper.autoplay.stop()
              }
          })
    } else if (container.dataset.autoplay === 'true') {
      const autoplaySpeed = parseInt(container.dataset.autoplaySpeed, 10) || 5000
      swiper.params.autoplay = {
        delay: autoplaySpeed,
        pauseOnMouseEnter: true
      };
      swiper.autoplay.start()
      container.addEventListener('mouseover', () => {
            if (!swiper.params) return
            swiper.params.autoplay = {
                delay: autoplaySpeed,
                pauseOnMouseEnter: true
            };
            swiper.autoplay.stop()
      });
      container.addEventListener('mouseleave', () => {
            if (!swiper.params) return
            swiper.params.autoplay = {
                delay: autoplaySpeed,
                pauseOnMouseEnter: true
            };
            swiper.autoplay.start()
      });
    }
  }

  setupBullets(container, swiper) {
    if (container.dataset.paginationType === 'bullets') {
        swiper.params.pagination.dynamicBullets = false;
        swiper.pagination.render();
        swiper.pagination.update();
    }
  }
}

const announcementBarSwiper = new MultiSwiper('.swiper-announcement', {
  a11y: {
    slideRole: 'listitem'
  },
  loop: true,
  navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
  }
})

const tabsSwiper = new MultiSwiper('.swiper-tabs', {
  loop: false,
  slidesPerView: 'auto'
})

class CountdownTimer extends HTMLElement {
  constructor() {
    super();

    const endDate = this.getAttribute('end-date'),
          endTime = this.getAttribute('end-time') || "00:00",
          timezoneOffset = this.getAttribute('timezone-offset'),
          expirationAction = this.getAttribute('expiration-action'),
          enableAnimation = this.getAttribute('enable-animation'),
          sectionId = this.getAttribute('section-id'),
          productHandle = this.getAttribute('product-handle');

    this.endDate = endDate;
    this.endTime = endTime;
    this.timezoneOffset = timezoneOffset;
    this.expirationAction = expirationAction;
    this.enableAnimation = enableAnimation === "true";
    this.sectionId = sectionId;
    this.productHandle = productHandle;
    this.animationDuration = 300;

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:load', this.onSectionLoad.bind(this))
    }

    this.init();
  }

  onSectionLoad(e) {
    const sectionsToRerender = ['shopify-section-announcement-bar'];
    const sectionElement = document.getElementById('shopify-section-' + e.detail.sectionId);
    
    if (sectionsToRerender.some(className => sectionElement.classList.contains(className))) {
      this.init();
    }
  }

  disconnectedCallback() {
    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:load', this.init)
    }
  }

  async init() {
    if (!this.endDate && this.productHandle) {
      await this.fetchEndDateTimeAndMessage();
    }

    this.deadlineTimestamp = new Date(`${this.endDate}T${this.endTime}`).getTime();

    const isDateValid = this.isDateValid(this.endDate);
    const isTimeValid = this.isTimeValid(this.endTime);
    const remainingTime = this.getRemainingTime(this.deadlineTimestamp);

    if (!isDateValid || !isTimeValid || !this.deadlineTimestamp || remainingTime.days > 99) {     
      if (Shopify.designMode) {
        this.classList.add('countdown--visible');

        return;
      } 
    
      this.removeCountdownTimer();
      
      return;
    }
    
    this.countdownTimer = this.querySelector('.countdown__timer');
    this.countdownCompleteMessage = this.querySelector('.countdown-complete-message');
    this.daysTens = this.querySelector('.countdown-days-tens');
    this.daysOnes = this.querySelector('.countdown-days-ones');
    this.hoursTens = this.querySelector('.countdown-hours-tens');
    this.hoursOnes = this.querySelector('.countdown-hours-ones');
    this.minutesTens = this.querySelector('.countdown-minutes-tens');
    this.minutesOnes = this.querySelector('.countdown-minutes-ones');
    this.secondsTens = this.querySelector('.countdown-seconds-tens');
    this.secondsOnes = this.querySelector('.countdown-seconds-ones');
    
    if (remainingTime.total > 0) {
      this.updateTimer();
      this.updateTimerIntervalId = setInterval(this.updateTimer.bind(this), 1000);
    } else if (this.productHandle) {
      this.onTimerExpire();
    } else if (this.expirationAction === 'hide_timer' || (this.expirationAction === 'show_message' && !this.countdownCompleteMessage)) {
      this.removeCountdownParent();
    }    

    this.classList.add('countdown--visible');   
  }

  async fetchEndDateTimeAndMessage() {
    await fetch(`/products/${this.productHandle}`)
    .then(response => response.text())
    .then(html => {
      const parser = new DOMParser();
      const timerOnProductPage = parser.parseFromString(html, 'text/html').querySelector('.countdown');

      const endDate = timerOnProductPage.getAttribute('end-date');
      const endTime = timerOnProductPage.getAttribute('end-time');
      
      this.setAttribute('end-date', endDate);
      this.setAttribute('end-time', endTime);
      
      const completeMessage = timerOnProductPage.getAttribute('complete-message');
      const completeMessageElement = this.querySelector('.countdown__complete-message');

      if (completeMessageElement) {
        completeMessageElement.innerHTML = completeMessage;
        this.setAttribute('complete-message', completeMessage);
      }
      
      this.endDate = endDate;
      this.endTime = endTime;

      const timerBlock = document.querySelector(`.countdown`);
      const timerBlockCopy = timerBlock.cloneNode(true);

      timerBlock.replaceWith(timerBlockCopy); // trigger rerender of countdown timer block
    })
    .catch(err => { 
      this.removeCountdownTimer();
    });
  }

  updateTimer() {
    const padWithZero = (num) => num.toString().padStart(2, '0');

    const remainingTime = this.getRemainingTime(this.deadlineTimestamp);
    const [days, hours, minutes, seconds] = [
      padWithZero(remainingTime.days),
      padWithZero(remainingTime.hours),
      padWithZero(remainingTime.minutes),
      padWithZero(remainingTime.seconds)
    ];
  
    const timerPartsMap = [
      { tens: this.daysTens, ones: this.daysOnes, value: days },
      { tens: this.hoursTens, ones: this.hoursOnes, value: hours },
      { tens: this.minutesTens, ones: this.minutesOnes, value: minutes },
      { tens: this.secondsTens, ones: this.secondsOnes, value: seconds }
    ];
  
    timerPartsMap.forEach(({ tens, ones, value }) => {
      this.animateNumberChange(tens, value[0]);
      this.animateNumberChange(ones, value[1]);
    });

    if (remainingTime.total <= 0) {
      setTimeout(() => this.onTimerExpire(), this.animationDuration);
    }
  }
  
  animateNumberChange(wrapper, newValue) {
    const currentNumber = wrapper.querySelector('.countdown__number--current');

    if (!this.enableAnimation) {
      currentNumber.innerText = newValue; 

      return;
    }

    const previousNumber = wrapper.querySelector('.countdown__number--previous');
  
    if (currentNumber.innerText !== newValue) {
      previousNumber.innerText = currentNumber.innerText; 
      currentNumber.innerText = newValue; 
  
      previousNumber.classList.add('countdown__number--animated');
      currentNumber.classList.add('countdown__number--animated');
  
      setTimeout(() => {
        previousNumber.classList.remove('countdown__number--animated');
        currentNumber.classList.remove('countdown__number--animated');
      }, this.animationDuration); 
    }
  }

  removeCountdownParent() {
    const sectionElement = document.getElementById('shopify-section-' + this.sectionId);

    if (!sectionElement) return;
    
    const sectionAssociationsMap = new Map([
      ['media-with-text', {
        contentWrapperClass: '.media-with-text__content-wrapper',
        removalTargetClass: '.section:has(.placeholder-svg)'
      }],
      ['section-image-banner', {
        contentWrapperClass: '.banner__content-wrapper',
        removalTargetClass: '.banner__content-wrapper'
      }],
      ['section-newsletter', {
        contentWrapperClass: '.banner__content-wrapper',
        removalTargetClass: '.banner__content-wrapper'
      }],
      ['section-video-banner', {
        contentWrapperClass: '.banner__content-wrapper',
        removalTargetClass: '.banner__content-wrapper'
      }],    
      ['rich-text', {
        contentWrapperClass: '.section-container',
        removalTargetClass: '.section-container'
      }],
      ['shopify-section-announcement-bar', {
        contentWrapperClass: '.announcement-block',
        removalTargetClass: '.announcement-block',
        sliderInstance: announcementBarSwiper,
        sectionWrapperClass: '.announcement-bar',
      }],
    ]);
    
    const matchingEntry = Array.from(sectionAssociationsMap).find(([sectionClass]) =>
      sectionElement.classList.contains(sectionClass)
    );

    if (matchingEntry) {
      const { contentWrapperClass, removalTargetClass, sliderInstance, sectionWrapperClass } = matchingEntry[1];
      const contentWrapper = sliderInstance ? this.closest(contentWrapperClass) : sectionElement.querySelector(contentWrapperClass);

      if (contentWrapper && this.getElementContentHeight(contentWrapper) === 0) {
        const elementToRemove = sliderInstance ? this.closest(removalTargetClass) : sectionElement.querySelector(removalTargetClass);

        if (!sliderInstance) {
          elementToRemove?.remove();

          return;
        }
        
        const sectionWrapper = sectionElement.querySelector(sectionWrapperClass);
        const isLastSlide = sectionElement.querySelectorAll(contentWrapperClass).length === 1;

        sliderInstance.removeSlide(elementToRemove?.dataset.swiperSlideIndex);

        if (sectionWrapper && this.getElementContentHeight(sectionWrapper) === 0 && isLastSlide) {         
          sectionWrapper.remove();
        }
      }
    }   
  }  

  removeCountdownTimer() {
    const countdownTimer = this.closest('.countdown-timer-wrapper');

    countdownTimer.style.display = 'none';

    this.removeCountdownParent();
    countdownTimer.remove();
  }

  onTimerExpire() {
    switch (this.expirationAction) {
      case "hide_timer":
        this.removeCountdownTimer();

        break;
      case "show_message":
        if (!this.countdownCompleteMessage) {
          this.removeCountdownTimer();

          break;
        }

        this.countdownTimer.remove();
        this.countdownCompleteMessage.removeAttribute('hidden');   
      
        break;
      case "show_zeros_and_message":
        if (this.countdownCompleteMessage) {
          this.countdownCompleteMessage.removeAttribute('hidden');
        }
        
        break;
    }

    clearInterval(this.updateTimerIntervalId);
  }

  getRemainingTime(deadline) {
    const total = deadline - this.getTimestampInStoreTimezone();
    const seconds = Math.floor((total / 1000) % 60);
    const minutes = Math.floor((total / 1000 / 60) % 60);
    const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
    const days = Math.floor(total / (1000 * 60 * 60 * 24));

    return {
      total,
      days,
      hours,
      minutes,
      seconds
    };
  }

  getTimestampInStoreTimezone() {
    const match = this.timezoneOffset.match(/([+-]?)(\d{2})(\d{2})/);
  
    if (!match) {
      console.error("Invalid timezone format:", this.timezoneOffset);
      return;
    }
  
    const now = new Date();
    const sign = match[1] === "-" ? -1 : 1;
    const hours = parseInt(match[2], 10);
    const minutes = parseInt(match[3], 10);
    const offsetMilliseconds = sign * ((hours * 60 + minutes) * 60000);
    const utcTimestamp = now.getTime() + now.getTimezoneOffset() * 60000; 
  
    return utcTimestamp + offsetMilliseconds;
  }

  getElementContentHeight(element) {
    const style = getComputedStyle(element);
    const paddingTop = parseFloat(style.paddingTop);
    const paddingBottom = parseFloat(style.paddingBottom);

    return element.clientHeight - paddingTop - paddingBottom;
  }

  isDatePatternValid(dateString) {
    return /^\d{4}-\d{2}-\d{2}$/.test(dateString); 
  }

  isTimePatternValid(timeString) {
    return /^\d{2}:\d{2}$/.test(timeString); 
  }

  isDateValid(dateString) {
    if (!this.isDatePatternValid(dateString)) return false;

    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day);

    return date.getFullYear() === year &&
            date.getMonth() === month - 1 &&
            date.getDate() === day;
  }

  isTimeValid(timeString) {
    if (!this.isTimePatternValid(timeString)) return false;

    const [hours, minutes] = timeString.split(":").map(Number);

    return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
  }
}

customElements.define('countdown-timer', CountdownTimer);


let sliders = document.querySelectorAll('.swiper-product-card');
function initializeSwiper(slider) {
  if (slider.dataset.initialized === 'true') return;
  slider.dataset.initialized = 'true';
  let speed = parseInt(slider.dataset.transitionDuration, 10) || 300;
  let autoplaySpeed = parseInt(slider.dataset.autoplaySpeed, 10) || 5000;
  let autoplay = slider.dataset.autoplay === "true";
  let loop = slider.dataset.loop === "true";
  let slidesLoaded = false;
  let direction
  let isDragging
  let cardSwiper;
  function initSwiper() {
      cardSwiper = new Swiper(slider, {
          a11y: {
            slideRole: 'listitem'
          },
          slidesPerView: 1,
          loop: loop,
          pagination: {
              el: slider.querySelector('.swiper-pagination'),
              type: 'bullets'
          },
          navigation: {
              nextEl: slider.querySelector('.swiper-button-next'),
              prevEl: slider.querySelector('.swiper-button-prev'),
          },
          speed: speed,
          autoplay: autoplay,
          lazy: {
              loadPrevNext: true,
          },
          roundLengths: false, 
          on: {
            touchMove: function () {
              setTimeout(() => {
                isDragging = true;
              }, 0);
            },
            touchEnd: function () {
              setTimeout(() => {
                  isDragging = false;
              }, 0);
            }
          }
      });
  }
  initSwiper();

  function loadAllSlides(slider, index) {
    if (!slider.classList.contains('swiper-loaded')) slider.classList.add('swiper-loaded')
      const totalSlides = parseInt(slider.dataset.totalSlides, 10);
      const productImages = slider.getAttribute('data-imgs').split(',');
      const productImageAlts = slider.getAttribute('data-img-alts').split(',');
      let imagesLoading = ''
      let preloadImages
      if(slider.hasAttribute('data-images-loading')) {
        imagesLoading = slider.getAttribute('data-images-loading');
        
        preloadImages = slider.getAttribute('data-preload-imgs')?.split(',');
      }
      if (imagesLoading == '' || imagesLoading == 'fade_scale' || imagesLoading == 'loader' || imagesLoading == 'disable') isSlideReady = true;
      for (let i = index; i < totalSlides; i++) {
          let image = productImages[i];
          let alt = productImageAlts[i];
          let preloadImage
          if (imagesLoading !== '' && preloadImages.length > 0) preloadImage = preloadImages[i];
          let newSlide = document.createElement("li");
          newSlide.classList.add('card__product-image', 'swiper-slide', 'aspect-ratio');
          if (imagesLoading != '' && imagesLoading != 'fade_scale') {
            newSlide.innerHTML = `<figure class="lazy-image lazy-image--${ imagesLoading }"><div class="lazy-image__preloader lazy-image__preloader--full lazy-image__preloader-${ imagesLoading }" aria-hidden="true"><img src="${preloadImage}" loading="eager" width="10" height="10" alt="${alt}"></div><img src="${image}" loading="lazy" onload="this.parentNode.classList.add('lazyloaded');" alt="${alt}"></figure>`;
          } else if (imagesLoading == 'fade_scale' ) {
            newSlide.innerHTML = `<figure class="lazy-image lazy-image--${ imagesLoading }"><img src="${image}" loading="lazy" onload="this.parentNode.classList.add('lazyloaded');" alt="${alt}"></figure>`;
          } else {
            newSlide.innerHTML = `<img src="${image}" alt="${alt}">`;
          }
          
          slider.querySelector('.swiper-wrapper').append(newSlide);
          if (imagesLoading !== '' && imagesLoading !== 'fade_scale' && imagesLoading !== 'loader') {
            const preloadImg = newSlide.querySelector('.lazy-image__preloader img');
            if (preloadImg) {
              isSlideReady = false;
              if (preloadImg.complete) {
                isSlideReady = true;
                if (pendingSlide === 'next') {
                  cardSwiper.slideNext();
                } else if (pendingSlide === 'prev') {
                  cardSwiper.slidePrev();
                }
                pendingSlide = null;
              } else {
                preloadImg.addEventListener('load', () => {
                  isSlideReady = true;
                  if (pendingSlide === 'next') {
                    cardSwiper.slideNext();
                  } else if (pendingSlide === 'prev') {
                    cardSwiper.slidePrev();
                  }
                  pendingSlide = null;
                });
              }
            }
          }
      }
      cardSwiper.update()
      recreateSwiper();

      if (window.innerWidth > 768 && autoplay) {
        slider.addEventListener('mouseenter', () => {
          cardSwiper.params.autoplay = {
            delay: autoplaySpeed,
            disableOnInteraction: false
          };
          cardSwiper.autoplay.start()
        });
        slider.addEventListener('mouseleave', () => {
          if (cardSwiper.autoplay.running) cardSwiper.autoplay.stop()
        })
      }
  }

  if (window.innerWidth > 768 && autoplay) {
    slider.addEventListener('mouseenter', () => {
      cardSwiper.params.autoplay = {
        delay: autoplaySpeed,
        disableOnInteraction: false
      };
      cardSwiper.autoplay.start()
    });
    slider.addEventListener('mouseleave', () => {
      if (cardSwiper.autoplay.running) cardSwiper.autoplay.stop()
    })
  }
  
  function loadAllSlidesByVariant(slider, currentAlt, swatchImageIndex) {
    if (!slider.classList.contains('swiper-loaded')) slider.classList.add('swiper-loaded')
    const sliderWrapper = slider.querySelector('.swiper-wrapper');
    const productImages = slider.getAttribute('data-imgs').split(',');
    const productImageAlts = slider.getAttribute('data-img-alts').split(',');

    sliderWrapper.innerHTML = ''; 

    const appendSlide = (image, alt = '') => {
        const newSlide = document.createElement("li");
        newSlide.classList.add('card__product-image', 'swiper-slide', 'aspect-ratio');
        newSlide.innerHTML = `<img src="${image}" alt="${alt}"><div class="swiper-lazy-preloader"></div>`;
        sliderWrapper.append(newSlide);
    };

    let filteredImages = productImages
        .map((image, index) => ({ image, alt: productImageAlts[index] }))
        .filter(({ alt }) => alt.includes(currentAlt));

    if (filteredImages.length === 0) {
        if (swatchImageIndex !== undefined && productImages[swatchImageIndex]) {
            filteredImages = [{ image: productImages[swatchImageIndex], alt: productImageAlts[swatchImageIndex] }];
        } else {
            filteredImages = productImages.map((image, index) => ({ image, alt: productImageAlts[index] }));
        }
    }

    filteredImages.forEach(({ image, alt }) => appendSlide(image, alt));

    recreateSwiper();
    slider.style.setProperty('--total-slides', sliderWrapper.children.length);
}

  function recreateSwiper() {
      if (cardSwiper) {
          cardSwiper.destroy(true, true); 
      }
      initSwiper();
  }

  if (slider.querySelector('.swiper-button-next')) slider.querySelector('.swiper-button-next').addEventListener('click', () => {
      if (!slidesLoaded) {
          loadAllSlides(slider, index = 1);
          slidesLoaded = true;
      }
      if (isSlideReady) {
        cardSwiper.slideNext();
      } else {
        pendingSlide = 'next';
      }
  });

  if (slider.querySelector('.swiper-button-prev')) slider.querySelector('.swiper-button-prev').addEventListener('click', (e) => {
      e.preventDefault();
      if (!slidesLoaded) {
          loadAllSlides(slider, index = 1);
          slidesLoaded = true;
      }
      if (isSlideReady) {
        cardSwiper.slidePrev();
      } else {
        pendingSlide = 'prev';
      }
  });

  if (slider.querySelector('.swiper-button-next')) slider.querySelector('.swiper-button-next').addEventListener('keyup', (e) => {
    if (e.code.toUpperCase() === 'ENTER') {
      if (!slidesLoaded) {
        loadAllSlides(slider, index = 1);
        slidesLoaded = true;
      }
      if (isSlideReady) {
        cardSwiper.slideNext();
      } else {
        pendingSlide = 'next';
      }
    }
  });

  if (slider.querySelector('.swiper-button-prev')) slider.querySelector('.swiper-button-prev').addEventListener('keyup', (e) => {
    if (e.code.toUpperCase() === 'ENTER') {
      e.preventDefault();
      if (!slidesLoaded) {
        loadAllSlides(slider, index = 1);
        slidesLoaded = true;
      }
      if (isSlideReady) {
        cardSwiper.slidePrev();
      } else {
        pendingSlide = 'prev';
      }
    } 
  });

  cardSwiper.on('sliderFirstMove', () => {
      const diffX = cardSwiper.touches.currentX - cardSwiper.touches.startX;
      const diffY = cardSwiper.touches.currentY - cardSwiper.touches.startY;
      if (Math.abs(diffX) > Math.abs(diffY)) {
          diffX > 0 ? direction = 'right' : direction = 'left'
      }
      if (!slidesLoaded) {
          loadAllSlides(slider, index = 1);
          slidesLoaded = true;
      }

      if (isSlideReady) {
        direction === 'left' ? cardSwiper.slideNext(300) : cardSwiper.slidePrev(300);
      } else {
        pendingSlide = direction === 'left' ? 'next' : 'prev';
      }
  });

  if (autoplay) slider.addEventListener('mouseenter', () => {
      if (!slidesLoaded) {
          loadAllSlides(slider, index = 1);
          slidesLoaded = true;
      }
  });
  slider.closest('.card').addEventListener('click', (e) => {
    if (isDragging) e.preventDefault();
  });
  slider.addEventListener('swiper:update', (event) => {
      const sliderWrapper = slider.querySelector('.swiper-wrapper');

      if (event.detail.currentAlt === 'all') {
          sliderWrapper.innerHTML = ''; 
          loadAllSlides(slider, index = 0);
      } else {
          loadAllSlidesByVariant(slider, event.detail.currentAlt, event.detail.index);
      }

      slidesLoaded = true;

      if (!slider.querySelector('.swiper-wrapper-only-variant')) {
        let variantFirstImageIndex = event.detail.index;

        if (!variantFirstImageIndex) {
          const productImageAlts = slider.getAttribute('data-img-alts').split(',');
          variantFirstImageIndex = productImageAlts.findIndex(alt => alt.includes(event.detail.currentAlt));
        }

        sliderWrapper.innerHTML = ''; 
        loadAllSlides(slider, index = 0);
        cardSwiper.slideTo(variantFirstImageIndex || 0, 0);
      }
  });
}
sliders.forEach(initializeSwiper);
document.addEventListener('shopify:section:load', (event) => {
  let sliders = event.target.querySelectorAll('.swiper-product-card');
  sliders.forEach(initializeSwiper)
  preloadImages()
})
document.addEventListener('ajax-page-load', (event) => {
  let sliders = document.querySelectorAll(`#shopify-section-${event.detail.sectionId} .swiper-product-card`);
  sliders.forEach(initializeSwiper)
  preloadImages()
})
document.addEventListener('filters-ajax-page-load', (event) => {
  let sliders = document.querySelectorAll(`.swiper-product-card`)
  sliders.forEach(initializeSwiper)
  preloadImages()
})
document.addEventListener('recommendations:loaded', () => {
  let sliders = document.querySelectorAll('.product-recommendations .swiper-product-card' )
  sliders.forEach(initializeSwiper)
  preloadImages()
})

// Slideshow
function initializeSlideshowSwiper(slideshow) {
  let effect = slideshow.dataset.effect;
  let speed = parseInt(slideshow.dataset.transitionDuration, 10) || 300
  let autoplaySpeed = parseInt(slideshow.dataset.autoplaySpeed, 10) || 5000
  let autoplay = slideshow.dataset.autoplay === "true"
  let isAutoplayRunning = !!autoplay;
  let progressCircle = slideshow.querySelector(".autoplay-progress__progress-circle");
  let autoplayProgress = slideshow.querySelector(".autoplay-progress");
  let progressIconPause = autoplayProgress?.querySelector(".autoplay-progress__video-control-icon--pause");
  let progressIconPlay = autoplayProgress?.querySelector(".autoplay-progress__video-control-icon--play");

  const slideshowSwiper = new Swiper(slideshow, {
      loop: true,
      slidesPerView: 1,
      pagination: {
          el: slideshow.querySelector('.swiper-pagination'),
          type: 'bullets',
          clickable: true,
      },
      navigation: {
          nextEl: slideshow.querySelector('.swiper-button-next'),
          prevEl: slideshow.querySelector('.swiper-button-prev'),
      },
      effect: effect,
      speed: speed,
      autoplay: autoplay ? {
          delay: autoplaySpeed,
          disableOnInteraction: false,
      } : false,
      creativeEffect: {
          prev: {
              shadow: true,
              translate: ["-20%", 0, -1],
          },
          next: {
              translate: ["100%", 0, 0],
          },
      },
      cardsEffect: {
          perSlideOffset: 5,
          perSlideRotate: 1
      },
      coverflowEffect: {
          slideShadows: false
      },
      flipEffect: {
          slideShadows: false
      },
      on: {
        autoplayTimeLeft(s, time, progress) {
          if(progressCircle) {
            progressCircle.style.setProperty("--progress", 1 - progress);
          }
        },
        slideChange() {
          progressIconPlay?.classList.add('hidden');
          progressIconPause?.classList.remove('hidden');
          isAutoplayRunning = true;
        }
      }
  })
  slideshow.querySelectorAll('video').forEach(video => video.load())

  autoplayProgress?.addEventListener("click", () => {
      if (isAutoplayRunning) {
          slideshowSwiper.autoplay.pause();
          progressIconPause?.classList.add('hidden');
          progressIconPlay?.classList.remove('hidden');
      } else {
          slideshowSwiper.autoplay.resume();
          progressIconPlay?.classList.add('hidden');
          progressIconPause?.classList.remove('hidden');
      }

      isAutoplayRunning = !isAutoplayRunning;
  });
}

function initAllSlideshowSwipers() {
  // document.querySelectorAll('.swiper-slideshow').forEach(initializeSlideshowSwiper);
  document.querySelectorAll('.swiper-slideshow').forEach(
    (slideshow) => {
      requestAnimationFrame(() => {
        initializeSlideshowSwiper(slideshow);
      });
    }
  );
}

document.addEventListener('DOMContentLoaded', initAllSlideshowSwipers);

document.addEventListener('shopify:section:load', (event) => {
  const slideshow = event.target.querySelector('.swiper-slideshow');
  if (slideshow) {
    requestAnimationFrame(() => {
      initializeSlideshowSwiper(slideshow);
    });
  }
});